import { IonBadge, IonRow, IonIcon } from '@ionic/react';
import { eyeOutline } from 'ionicons/icons';
import { TeamMember, TeamAgent } from '../../types/teamSummary';
import { ProfileStatus } from '../../types/profile';
import { formatLastLogin } from '../../utils/teamData';
import { RowActionMenu } from './RowActionMenu';
import { AgentActionMenu } from './AgentActionMenu';
import { getScoreColor } from '../mystery-shop/utils';

interface TeamMemberTableProps {
  users: TeamMember[];
  agents?: TeamAgent[];
  shop?: boolean;
  onViewBadges: (event: React.MouseEvent, member: TeamMember) => void;
  onViewCertifications: (event: React.MouseEvent, member: TeamMember) => void;
  onRemove: (event: React.MouseEvent, member: TeamMember) => void;
  onSuspend: (event: React.MouseEvent, member: TeamMember) => void;
  onActivate: (event: React.MouseEvent, member: TeamMember) => void;
  onViewActivityLog: (event: React.MouseEvent, member: TeamMember) => void;
  onViewCourses: (event: React.MouseEvent, member: TeamMember) => void;
  onAssociateAgentToUser?: (event: React.MouseEvent, agent: TeamAgent) => void;
}

export function TeamMemberTable({
  users,
  agents = [],
  shop,
  onViewBadges,
  onViewCertifications,
  onRemove,
  onSuspend,
  onActivate,
  onViewActivityLog,
  onViewCourses,
  onAssociateAgentToUser
}: TeamMemberTableProps) {

  return (
    <div className="rounded-b-lg shadow overflow-hidden border-t-0">
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Table Header */}
          <IonRow className="font-semibold border-b border-gray-200 flex">
            <div className="flex-1 basis-0 min-w-0 py-3 px-4">
              <span className="text-sm text-gray-700">User</span>
            </div>
            <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
              <span className="text-sm text-gray-700">Status</span>
            </div>
            <div className="flex-1 basis-0 min-w-0 py-3 px-4">
              <span className="text-sm text-gray-700">Last Login</span>
            </div>
            <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
              <span className="text-sm text-gray-700">Courses<br />Completed</span>
            </div>
            <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
              <span className="text-sm text-gray-700">Progress %</span>
            </div>
            <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
              <span className="text-sm text-gray-700">Badges<br />Earned</span>
            </div>
            {shop && (
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                <span className="text-sm text-gray-700">Calls</span>
              </div>
            )}
            {shop && (
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                <span className="text-sm text-gray-700">Avg Score</span>
              </div>
            )}
            {shop && (
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                <span className="text-sm text-gray-700">Trend</span>
              </div>
            )}
            <div className="w-24 flex-shrink-0 py-3 px-4 text-center">
              {/* Actions column */}
            </div>
          </IonRow>

          {/* Table Body */}
          {users.map((member, index) => (
            <IonRow
              key={member.id || `member-${index}`}
              className={`border-b border-gray-200 hover:bg-gray-50 transition-colors flex
                ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <div className="flex-1 basis-0 min-w-0 py-3 px-4">
                <span className="font-semibold text-[#231F20] text-base truncate block" title={member.displayName}>
                  {member.displayName}
                </span>
              </div>
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                {member.status === ProfileStatus.Suspended ? (
                  <IonBadge
                    className="px-2 py-1 text-xs font-semibold"
                    style={{
                      '--background': '#D44D5C',
                      '--color': '#000000'
                    } as React.CSSProperties}
                  >
                    Suspended
                  </IonBadge>
                ) : (
                  <IonBadge
                    color={member.status === ProfileStatus.Pending ? 'warning' : 'medium'}
                    className="px-2 py-1 text-xs font-semibold"
                  >
                    {member.status === ProfileStatus.Pending ? 'Pending' : 'Active'}
                  </IonBadge>
                )}
              </div>
              <div className="flex-1 basis-0 min-w-0 py-3 px-4">
                <span className="text-gray-600 text-sm whitespace-nowrap">
                  {formatLastLogin(member.lastSeen)}
                </span>
              </div>
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                <IonBadge color="primary" className="px-3 py-1.5 text-sm font-semibold">
                  {member.totalCoursesCompleted}
                </IonBadge>
              </div>
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                <span className="text-[#231F20] text-base font-semibold">
                  {member.progress ?? 0}%
                </span>
              </div>
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                <button
                  onClick={(e) => onViewBadges(e, member)}
                  className="inline-flex items-center justify-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
                >
                  <span>🏆</span>
                  <span>{member.badges?.length || 0}</span>
                </button>
              </div>
              {shop && (
                <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                  <span className="text-gray-600 text-sm">
                    {member.agentPerformance?.callCount ?? 0}
                  </span>
                </div>
              )}
              {shop && (
                <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                  {member.agentPerformance?.avgScore != null ? (
                    <span className="font-semibold" style={{ color: getScoreColor(member.agentPerformance.avgScore) }}>
                      {member.agentPerformance.avgScore.toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              )}
              {shop && (
                <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                  {member.agentPerformance?.trend != null ? (
                    <span className={member.agentPerformance.trend >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {member.agentPerformance.trend >= 0 ? '↑' : '↓'} {Math.abs(member.agentPerformance.trend).toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              )}
              <div className="w-24 flex-shrink-0 py-3 px-4 text-center">
                <div className="flex items-center justify-center gap-4">
                  <IonIcon 
                    icon={eyeOutline} 
                    className="text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                    style={{ fontSize: '1.25rem' }}
                    onClick={(e) => onViewCourses(e as any, member)}
                  />
                  <RowActionMenu
                    member={member}
                    onRemove={onRemove}
                    onSuspend={onSuspend}
                    onActivate={onActivate}
                    onViewActivityLog={onViewActivityLog}
                  />
                </div>
              </div>
            </IonRow>
          ))}

          {/* Agent Rows (standalone agents, performance data only) */}
          {agents.map((agent, index) => (
            <IonRow
              key={agent.id || `agent-${index}`}
              className={`border-b border-gray-200 hover:bg-gray-50 transition-colors flex
                ${(users.length + index) % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <div className="flex-1 basis-0 min-w-0 py-3 px-4">
                <span className="font-semibold text-[#231F20] text-base truncate block" title={agent.displayName}>
                  {agent.displayName}
                </span>
              </div>
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                <span className="text-sm text-gray-600">Agent</span>
              </div>
              <div className="flex-1 basis-0 min-w-0 py-3 px-4" />
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4" />
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4" />
              <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4" />
              {shop && (
                <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                  <span className="text-gray-600 text-sm">
                    {agent.agentPerformance?.callCount ?? 0}
                  </span>
                </div>
              )}
              {shop && (
                <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                  {agent.agentPerformance?.avgScore != null ? (
                    <span className="font-semibold" style={{ color: getScoreColor(agent.agentPerformance.avgScore) }}>
                      {agent.agentPerformance.avgScore.toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              )}
              {shop && (
                <div className="flex-1 flex-shrink-0 min-w-0 py-3 px-4 text-center">
                  {agent.agentPerformance?.trend != null ? (
                    <span className={agent.agentPerformance.trend >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {agent.agentPerformance.trend >= 0 ? '↑' : '↓'} {Math.abs(agent.agentPerformance.trend).toFixed(0)}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              )}
              <div className="w-24 flex-shrink-0 py-3 px-4 text-center">
                <AgentActionMenu
                  onAssociateToUser={(e) => onAssociateAgentToUser?.(e, agent)}
                />
              </div>
            </IonRow>
          ))}
        </div>
      </div>
    </div>
  );
}
